/**
 * @file vuexstore to handle draft posts
 * Draft posts are show in the post modal but not saved to the server yet
 * This module is dependent on the post module to get cid of the post being edited
 */

import { __ } from 'bits/intl'
import sortBy from 'lodash.sortby'
import buildPost from 'surface/build_post'
import { Cid, DraftPost, Post, PostAttributes } from 'types'
import Vue from 'vue'
import { Module } from 'vuex'
import { isNew } from 'vuexstore/helpers/post'
import { RootState } from 'vuexstore/surface/types'

interface DraftState {
  draftByCid: Record<Cid, DraftPost>
  // when a minimized draft has no subject, we will use "Draft 1", "Draft 2", ... as its title,
  // this counter is to keep track of the number to be used
  minimizedDraftPlaceholderTitleCount: number
  activeDraftUploadingFile: File | null
}

const initialState = (): DraftState => {
  return {
    draftByCid: {},
    minimizedDraftPlaceholderTitleCount: 0,
    activeDraftUploadingFile: null,
  }
}

const draft: Module<DraftState, RootState> = {
  namespaced: true,
  state: initialState,
  getters: {
    draftByCid: (state): Function => (cid: Cid): DraftPost | null => state.draftByCid[cid],
    // Draft posts that are not being edited, i.e. minimized
    standByDraftCids: (state, getters, _, rootGetters): Cid[] =>
      sortBy(
        Object.keys(state.draftByCid).filter((postCid) => postCid !== rootGetters['post/postBeingEditedCid']),
        (postCid) => getters.draftByCid(postCid).lastDraftedAt,
      ),
    // The draft post being edited
    activeDraft: (state, getters, _, rootGetters): DraftPost | null => {
      return rootGetters['post/postBeingEditedCid'] ? getters.draftByCid(rootGetters['post/postBeingEditedCid']) : null
    },
    activeDraftCid: (state, getters): Cid | null => getters.activeDraft?.cid,
    activeDraftUploadingFile: (state): File | null => state.activeDraftUploadingFile,
    isCidBeingDrafted: (state): Function => (cid: Cid): boolean => !!state.draftByCid[cid],
    isDraftingNewPost: (state, getters): boolean => !!getters.activeDraft && isNew(getters.activeDraft),
  },
  mutations: {
    UPDATE_DRAFT(state, post: DraftPost): void {
      Vue.set(state.draftByCid, post.cid, post)
    },
    REMOVE_DRAFT(state, postCid: Cid): void {
      Vue.delete(state.draftByCid, postCid)
    },
    SAVE_DRAFT_PLACEHOLDER_TITLE(state, postCid: Cid): void {
      state.minimizedDraftPlaceholderTitleCount += 1
      const post = state.draftByCid[postCid]
      Vue.set(state.draftByCid, post.cid, {
        ...post,
        minimizedPlaceholderTitle: __(`Draft %{draftNumber}`, {
          draftNumber: state.minimizedDraftPlaceholderTitleCount,
        }),
      })
    },
    REMOVE_DRAFT_ATTACHMENT(state, { postCid }: { postCid: Cid }): void {
      Vue.set(state.draftByCid[postCid], 'attachment', '')
      Vue.set(state.draftByCid[postCid], 'attachment_caption', null)
    },
    SET_ACTIVE_DRAFT_UPLOADING_FILE(state, { file }: { file: File | null }): void {
      state.activeDraftUploadingFile = file
    },
  },
  actions: {
    updateDraft({ commit, getters }, post: Post | DraftPost | PostAttributes): void {
      commit('UPDATE_DRAFT', { ...getters.activeDraft, ...post, lastDraftedAt: Date.now() })
    },
    removeDraft({ commit, dispatch, getters }, postCid: Cid): void {
      if (postCid === getters.activeDraftCid) {
        dispatch('stopUploadFileActiveDraft')
        dispatch('post/stopEditingPost', null, { root: true })
      }
      commit('REMOVE_DRAFT', postCid)
    },
    setDraftNumber({ commit, getters, rootGetters }): void {
      // set the title for minimized draft to "Draft 1", "Draft 2", ... if necessary
      const draftPost = rootGetters['post/postBeingEditedCid']
        ? getters.draftByCid(rootGetters['post/postBeingEditedCid'])
        : null
      if (draftPost && !draftPost.subject && !draftPost.minimizedPlaceholderTitle) {
        commit('SAVE_DRAFT_PLACEHOLDER_TITLE', rootGetters['post/postBeingEditedCid'])
      }
    },
    completeDraftUpload({ dispatch, rootState, rootGetters }, { file, uploadedUrl }): void {
      let completedPostAttributes = rootGetters['post/pendingPosts'].find((post): boolean => post.file === file)
      dispatch('post/removePendingPost', completedPostAttributes, { root: true })
      if (uploadedUrl) {
        completedPostAttributes = buildPost(completedPostAttributes, {
          state: rootState,
          getters: rootGetters,
        })

        dispatch('post/updatePostInStore', completedPostAttributes, { root: true })
        dispatch('updateDraft', { ...completedPostAttributes, subject: file.name, attachment: uploadedUrl })
      }
    },
    cancelEditingDraft({ dispatch, getters }): void {
      if (getters.activeDraftCid) {
        if (isNew(getters.activeDraft)) {
          dispatch('post/deletePost', { postCid: getters.activeDraftCid }, { root: true })
        }
        dispatch('removeDraft', getters.activeDraftCid)
      }
    },
    removeDraftAttachment({ commit, getters }, { postCid }: { postCid: Cid }): void {
      if (getters.draftByCid(postCid)) {
        commit('REMOVE_DRAFT_ATTACHMENT', { postCid })
      }
    },
    uploadFileActiveDraft({ commit }, { file }: { file: File }): void {
      commit('SET_ACTIVE_DRAFT_UPLOADING_FILE', { file })
    },
    stopUploadFileActiveDraft({ commit }): void {
      commit('SET_ACTIVE_DRAFT_UPLOADING_FILE', { file: null })
    },
  },
}

export default draft
export { DraftState }
